import {
  BookingWidgetFieldSettings,
  ButtonModuleSettings,
  DefaultModuleSettings,
  EnquiryWidgetFieldSettings,
  ImageCropParams,
  LinkData,
  NewsletterModuleFieldSettings,
  OfferingsModuleSettings,
  Point,
  QuickEnquiryWidgetFieldSettings,
  TextAligns,
  TitleAndSubtitleTextAlign,
} from "../../types/index.js";

export const actionPrefix = "modules";

export const defaultImportantImagePoint: Point = { x: 0.5, y: 0.5 };
export const defaultImageCropParams: ImageCropParams = {
  x: 0,
  y: 0,
  width: 1,
  height: 1,
};

const linkData: LinkData = {
  url: null,
  moduleId: null,
  moduleType: null,
  pageId: null,
  languageId: null,
};

const buttonModuleSettings: ButtonModuleSettings = {
  global: { layout: "flat", corner: "square", type: "primary" },
  language: { title: "Button", linkData },
};

export const defaultFieldSettings: {
  BookingModule: BookingWidgetFieldSettings;
  EnquiryModule: EnquiryWidgetFieldSettings;
  QuickEnquiryModule: QuickEnquiryWidgetFieldSettings;
  NewsletterModule: NewsletterModuleFieldSettings;
} = {
  BookingModule: {
    phone: "optional",
    street: "disabled",
    zipcode: "disabled",
    city: "disabled",
    country: "optional",
    note: "optional",
  },
  EnquiryModule: {
    stay: "required",
    occupancies: "required",
    phone: "optional",
    street: "disabled",
    zipcode: "disabled",
    city: "disabled",
    country: "optional",
    note: "optional",
    newsletter: "optional",
  },
  QuickEnquiryModule: { stay: "required", guests: "required" },
  NewsletterModule: {
    gender: "optional",
    firstName: "optional",
    lastName: "optional",
    country: "optional",
  },
};

const textAlign: TextAligns = {
  title: "center",
  subtitle: "center",
  description: "center",
  buttons: "center",
};

const highlightTextAlign: TextAligns = {
  title: "left",
  subtitle: "left",
  description: "left",
  buttons: "right",
};

const titleAndSubtitleTextAlign: TitleAndSubtitleTextAlign = {
  title: textAlign.title,
  subtitle: textAlign.subtitle,
};

const offeringModuleSettings: OfferingsModuleSettings = {
  language: { title: "", subtitle: "" },
  global: {
    textAlign: titleAndSubtitleTextAlign,
    layout: "layout_1",
    displayType: "tiles",
    mediaAspectRatio: 1.3333,
    maxColumns: 4,
    pinnedItems: [],
    maxItems: undefined,
    enquiryLayout: "layout_1",
    bookingLayout: "compact",
  },
};

export const defaultSettings: DefaultModuleSettings = {
  HeaderModule: {
    language: {
      activeActionModuleIds: {},
      links: { vouchers: linkData, specials: linkData, booking: linkData },
    },
    global: {
      layoutVariant: "all-in-header",
      showBreadcrumb: false,
      sticky: false,
      menuVariant: "bottom-bar",
      topHeaderVariant: "hamburger-nav",
      showMenuSeparators: false,
      showSouthTyrolLogo: true,
      logoId: undefined,
      logoBackground: "transparent",
      imageOverlayGradient: "dark",
      whatsAppNumber: undefined,
      logoSize: "medium",
      bookingLinkKind: "actionModule",
      useFaviconAsLogo: false,
    },
  },
  FooterModule: {
    language: { title: "Kontaktieren Sie uns", description: undefined },
    global: {
      logoCategories: [["047c323d-c032-4a27-ada5-a67d6aebb507"], []],
      layoutVariant: "big",
      logoSize: "big",
      showTrustYou: true,
      textAlign: { ...textAlign, title: "left", description: "left" },
    },
  },
  ButtonModule: buttonModuleSettings,
  TextModule: {
    language: {
      title: "Lorem ipsum dolor sit amet",
      subtitle:
        "Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam",
      description: undefined,
    },
    global: {
      mediaAspectRatio: 1.7778,
      textAlign,
      mediaAlign: "left",
      width: "container-width",
      columns: 1,
      boxAlign: "center",
    },
  },
  OverlayModule: {
    language: { title: "", subtitle: "" },
    global: {
      fontSize: "medium",
      horizontalAlign: "left",
      textStyle: "plain",
      verticalAlign: "bottom",
    },
  },
  ImagesModule: {
    language: { title: "", subtitle: "" },
    global: {
      columnsCount: 1,
      width: 6,
      gap: "none",
      mediaAspectRatio: 1.7778,
      imagesType: "separator",
      sliderEffect: "slide",
      textAlign: titleAndSubtitleTextAlign,
    },
  },
  ImageModule: {
    global: {
      crop: defaultImageCropParams,
      importantPoint: defaultImportantImagePoint,
      pictureId: "",
    },
    language: {},
  },
  ImageGalleryModule: {
    language: { title: "", subtitle: "" },
    global: {
      mediaAspectRatio: 1.7778,
      layout: "layout-1",
      textAlign: titleAndSubtitleTextAlign,
    },
  },
  RoomsModule: {
    ...offeringModuleSettings,
    global: { ...offeringModuleSettings.global, layout: "layout_4" },
  },
  MapModule: { language: {}, global: { zoom: 15 } },
  OSMModule: { language: {}, global: { zoom: 15 } },
  BookingModule: {
    language: { title: "", subtitle: "" },
    global: {
      license: "extended",
      defaultOfferList: "rooms",
      layout: "one_pager",
      textAlign: titleAndSubtitleTextAlign,
      fieldSettings: defaultFieldSettings.BookingModule,
    },
  },
  PortalModule: {
    language: { title: "", subtitle: "" },
    global: { textAlign: titleAndSubtitleTextAlign, id: "", showSearch: true },
  },
  NotFoundTextModule: { language: {}, global: {} },
  SpecialsModule: offeringModuleSettings,
  PriceModule: {
    language: { title: "Preisliste", subtitle: "", description: undefined },
    global: {
      layout: "layout_1",
      grouping: "board",
      offerIds: [],
      roomIds: [],
      boardIds: [],
      textAlign,
    },
  },
  EnquiryModule: {
    language: { title: "Unverbindliche Anfrage", subtitle: "" },
    global: {
      textAlign: titleAndSubtitleTextAlign,
      fieldSettings: defaultFieldSettings.EnquiryModule,
      layout: "layout_1",
    },
  },
  QuickEnquiryModule: {
    language: {},
    global: {
      badgeStyle: "text",
      layout: "bottom_left",
      fieldSettings: defaultFieldSettings.QuickEnquiryModule,
    },
  },
  HighlightsModule: {
    language: { title: "", subtitle: "" },
    global: {
      textAlign: titleAndSubtitleTextAlign,
      layout: "layout-1",
      displayType: "list",
      mediaAspectRatio: 1.3333,
      maxColumnsCount: 2,
      collapsedLinesCount: undefined,
      transparentSliderArrowBackground: false,
      mediaAlign: "left",
    },
  },
  HighlightModule: {
    language: { title: "Lorem ipsum", subtitle: "", description: undefined },
    global: { textAlign: highlightTextAlign },
  },
  ImprintModule: {
    language: { legalAddress: "" },
    global: {
      legalForm: undefined,
      paidUpShareCapital: undefined,
      pecAddress: "",
      reaNumber: "",
      taxCode: "",
    },
  },
  PrivacyModule: {
    language: { title: "Datenschutz", description: undefined },
    global: { textAlign },
  },
  TermsModule: { language: {}, global: {} },
  VideoModule: {
    language: { title: "", subtitle: "" },
    global: {
      textAlign: titleAndSubtitleTextAlign,
      videoUrl: "",
      controls: true,
      showInfo: false,
      autoPlay: false,
      startAt: "00:00",
      size: "fullWidth",
      mute: false,
    },
  },
  EasiPayModule: {
    language: { title: "", subtitle: "" },
    global: { textAlign: titleAndSubtitleTextAlign, authId: "" },
  },
  EasiFastCheckInModule: {
    language: { title: "", subtitle: "" },
    global: { url: "", textAlign: titleAndSubtitleTextAlign },
  },
  HTMLModule: {
    language: { title: "Lorem ipsum dolor sit amet", subtitle: "", html: "" },
    global: { textAlign: titleAndSubtitleTextAlign },
  },
  MTSToursModule: {
    language: { title: "", subtitle: "" },
    global: { textAlign: titleAndSubtitleTextAlign, archive: "", user: "" },
  },
  GastroPoolInsuranceModule: {
    language: { title: "", subtitle: "", key: "" },
    global: { textAlign: titleAndSubtitleTextAlign },
  },
  SeparatorModule: {
    language: {},
    global: {
      icon: "tree-1",
      width: "container-width",
      height: "medium",
      iconCount: 1,
      iconSize: "medium",
      lineStyle: "straight",
      lineWeight: "light",
    },
  },
  WeatherWebcamModule: { language: {}, global: { layoutVariant: "vertical" } },
  WeatherModule: {
    language: { title: "Lorem ipsum", subtitle: "" },
    global: { districtId: undefined, iconStyle: "colored", textAlign },
  },
  WebcamModule: {
    language: {
      title: "",
      subtitle: "",
      description: undefined,
      imageCaption: "",
    },
    global: { url: "", textAlign, layoutVariant: "image-first", size: "big" },
  },
  Google360VirtualTourModule: {
    language: { title: "Lorem ipsum dolor sit amet", subtitle: "" },
    global: { textAlign, url: undefined },
  },
  EmbeddedGoogleMapsModule: { language: { url: undefined }, global: {} },
  PopUpModule: {
    language: {
      title: "Lorem ipsum dolor sit amet",
      description: undefined,
      subtitle: "",
    },
    global: {
      mediaAspectRatio: 1.7778,
      textAlign,
      imageAlign: "top",
      boxAlign: "center",
      darkBackground: true,
      onlyShowWhenUserInactive: false,
    },
  },
  NewsletterModule: {
    language: { title: "Newsletter", subtitle: "", description: undefined },
    global: {
      textAlign,
      fieldSettings: defaultFieldSettings.NewsletterModule,
      layout: "layout-1",
    },
  },
  PeerTvModule: {
    language: {
      title: "",
      subtitle: "",
      caption: "",
      scriptUrl: "",
      websiteUrl: "",
    },
    global: { textAlign, size: "big" },
  },
  ACSmartVoucherModule: {
    language: { title: "", subtitle: "" },
    global: { textAlign, cssUrl: "", kID: "" },
  },
  QuestionsAndAnswersModule: {
    language: { title: "Lorem ipsum", subtitle: "" },
    global: { textAlign: titleAndSubtitleTextAlign },
  },
  QuestionAndAnswerModule: {
    language: {
      title: "Lorem ipsum",
      description: undefined,
      plainDescription: undefined,
    },
    global: { textAlign: highlightTextAlign },
  },
  GuestnetWidgetModule: {
    language: { title: "", subtitle: "" },
    global: { textAlign: titleAndSubtitleTextAlign, baseUrl: "" },
  },
  HogastGastropoolPayModule: {
    language: { title: "", subtitle: "" },
    global: { textAlign: titleAndSubtitleTextAlign, key: "" },
  },
  HogastGastropoolSmartPayModule: {
    language: { title: "", subtitle: "" },
    global: {
      textAlign: titleAndSubtitleTextAlign,
      key: "",
      provider: "hogast",
      options: { deposit: true, travelInsurance: true },
    },
  },
  QuoteModule: {
    language: {
      title: "",
      subtitle: "",
      author: "Lorem ipsum",
      quote:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Nesciunt, quod.",
    },
    global: {
      fontFamily: null,
      textAlign,
      mediaAlign: "left",
      layout: "layout-1",
      fontSize: "medium",
    },
  },
  AccommodationFeaturesModule: {
    global: {
      textAlign: titleAndSubtitleTextAlign,
      collapsedLinesCount: undefined,
      maxColumns: 3,
      pinnedItems: [],
    },
    language: { title: "Lorem ipsum dolor sit amet", subtitle: "" },
  },
  ArriveInSouthTyrolModule: {
    language: { title: "", subtitle: "" },
    global: { textAlign: titleAndSubtitleTextAlign },
  },
  GetavoModule: {
    language: { title: "", subtitle: "" },
    global: { textAlign: titleAndSubtitleTextAlign, code: "" },
  },
  BrandnamicVoucherAppModule: {
    language: { title: "", subtitle: "" },
    global: { textAlign: titleAndSubtitleTextAlign, code: "" },
  },
};
